import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import { TextField as FormikTextField, TextField } from 'formik-material-ui';
import { navigate } from 'gatsby';
import { connect } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
} from '@material-ui/core';
import { getOr } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import CustomInputField from '../custom/CustomInputField';
import { RegistrationValidation } from './validation';
import { auth, alert } from '../../state';

import ReCaptcha from '../ReCaptcha';
import PhoneInput from '../custom/PhoneInput';
import { timezones } from '../../utils/constants';
import ConfirmationDialog from '../routes/GiftCardDetails/ConfirmDialog';

const RegistrationForm = ({ register, setAlert, setUser }) => {
  const { t } = useTranslation();
  const recaptcha = React.createRef();
  const activeEnv =
    process.env.GATSBY_ACTIVE_ENV || process.env.NODE_ENV || 'development';
  const [setShowCaptcha] = useState(activeEnv !== 'development');
  const [isConfirm, setIsConfirm] = useState(false);

  return (
    <>
      <Formik
        enableReinitialize
        validationSchema={RegistrationValidation}
        initialValues={{
          firstName: '',
          lastName: '',
          merchantName: '',
          email: '',
          phone: '',
          countryCode: 'US',
          timezone: '',
          password: '',
          confirmPassword: '',
          acceptTerms: true,
          token: '',
        }}
        onSubmit={async (values, actions) => {
          const email = { email: values.email };
          const response = await register(values);
          const successMsg = getOr('', ['payload', 'message'], response);
          const error = getOr('', ['error', 'message'], response);
          if (error) {
            if (error.includes('associated with an incomplete registration')) {
              setIsConfirm(error);
            } else {
              setAlert({ message: error, severity: 'error' });
            }
            actions.setSubmitting(false);
          }
          if (!error) {
            setUser(email);
            setAlert({ message: successMsg, severity: 'success' });
            navigate('/verify-registration');
          }
          recaptcha.current.props.grecaptcha.reset();
        }}
      >
        {({ isSubmitting, handleSubmit, setFieldValue, values }) => {
          return (
            <Form>
              <Box pb={2}>
                <Grid container direction="row" spacing={2}>
                  <Grid item xs={12} md={6}>
                    <CustomInputField
                      customInput={Field}
                      component={TextField}
                      capitalize
                      fullWidth
                      required
                      label={t('firstName')}
                      name="firstName"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CustomInputField
                      customInput={Field}
                      component={TextField}
                      capitalize
                      fullWidth
                      required
                      label={t('lastName')}
                      name="lastName"
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box pb={2}>
                <CustomInputField
                  customInput={Field}
                  component={TextField}
                  fullWidth
                  required
                  capitalize
                  label={t('businessName')}
                  name="merchantName"
                  variant="outlined"
                />
              </Box>
              <Box pb={2}>
                <PhoneInput
                  value={values.countryCode}
                  onChange={(value) => {
                    setFieldValue('phone', value);
                  }}
                />
              </Box>
              <Box pb={2}>
                <Field
                  component={FormikTextField}
                  fullWidth
                  select
                  name="timezone"
                  label={t('timezone')}
                  variant="outlined"
                  required
                >
                  {timezones.map((tz) => (
                    <MenuItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </MenuItem>
                  ))}
                </Field>
              </Box>
              <Box pb={2}>
                <CustomInputField
                  customInput={Field}
                  component={TextField}
                  trim
                  fullWidth
                  required
                  label={t('email')}
                  name="email"
                  variant="outlined"
                  helperText="* We recommend using an e-mail associated with this business"
                />
              </Box>
              <Box pb={2}>
                <Field
                  component={TextField}
                  fullWidth
                  required
                  label={t('password')}
                  type="password"
                  name="password"
                  variant="outlined"
                />
              </Box>
              <Box pb={2}>
                <Field
                  component={TextField}
                  fullWidth
                  required
                  label={t('confirmPassword')}
                  type="password"
                  name="confirmPassword"
                  variant="outlined"
                />
              </Box>
              <Box pb={2}>
                {/* <ReCaptcha onResolved={onResolved} createRef={recaptcha} /> */}
                <ReCaptcha
                  onResolved={(token) => {
                    setShowCaptcha(false);
                    console.log('token', token);
                    setFieldValue('token', token);
                  }}
                  createRef={recaptcha}
                />
              </Box>
              <Box>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  size="large"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} />
                  ) : (
                    t('register')
                  )}
                </Button>
              </Box>
            </Form>
          );
        }}
      </Formik>
      <ConfirmationDialog
        open={!!isConfirm}
        setOpen={setIsConfirm}
        content={isConfirm}
        isConfirmation={false}
        actionOk={() => navigate('/forgot-password')}
      />
    </>
  );
};

RegistrationForm.propTypes = {
  register: PropTypes.func.isRequired,
  setAlert: PropTypes.func.isRequired,
  setUser: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  register: (payload) => dispatch(auth.actions.register(payload)),
  setUser: (payload) => dispatch(auth.actions.setUser(payload)),
  setAlert: (payload) => dispatch(alert.actions.open(payload)),
});

export default connect(null, mapDispatchToProps)(RegistrationForm);
